'use client';

import { createContext, useContext, useEffect, useRef, useState, ReactNode } from 'react';
import { User } from '@supabase/supabase-js';
import { supabase } from '@/services/supabase/supabase';
import useUserStore from '@/states/user-store';
import userService from '@/services/supabase/user.service';
import auditService from '@/services/supabase/audit.service';

interface AuthContextType {
  user: User | null;
  loading: boolean;
  profileLoading: boolean;
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  loading: true,
  profileLoading: false,
});

export const useAuthContext = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuthContext must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [profileLoading, setProfileLoading] = useState(false);
  const { setUser: setUserStore, logout: logoutStore, setIsAuthenticated } = useUserStore();
  const [isInitialized, setIsInitialized] = useState(false);
  const isInitializedRef = useRef(false);
  const currentUserIdRef = useRef<string | null>(null);

  useEffect(() => {
    let isMounted = true;
    let profileLoadFor: string | null = null;

    const safetyTimeout = setTimeout(() => {
      if (isMounted && !isInitializedRef.current) {
        console.warn('AuthProvider: Safety timeout reached – forcing loading to false');
        setLoading(false);
        isInitializedRef.current = true;
        setIsInitialized(true);
      }
    }, 5000);

    const finishInitialization = () => {
      isInitializedRef.current = true;
      setLoading(false);
      setIsInitialized(true);
      clearTimeout(safetyTimeout);
    };

    const loadProfile = (userId: string) => {
      if (profileLoadFor === userId) return;
      profileLoadFor = userId;
      setProfileLoading(true);

      let timeout: ReturnType<typeof setTimeout> | undefined;
      const timeoutPromise = new Promise<null>(resolve => {
        timeout = setTimeout(() => resolve(null), 5000);
      });

      void Promise.race([userService.getUserById(userId), timeoutPromise]).then(profile => {
        if (isMounted && currentUserIdRef.current === userId && profile) {
          setUserStore(profile);
        } else if (isMounted && currentUserIdRef.current === userId) {
          console.warn('AuthProvider: User profile is unavailable');
        }
      }).catch(error => {
        console.warn('Background profile load note:', error);
      }).finally(() => {
        if (timeout) clearTimeout(timeout);
        if (profileLoadFor === userId) profileLoadFor = null;
        if (isMounted && currentUserIdRef.current === userId) setProfileLoading(false);
      });
    };

    // Get initial session
    const getInitialSession = async () => {
      try {
        const { data: { session }, error } = await supabase.auth.getSession();

        if (!isMounted) return;

        if (error) {
          console.error('AuthProvider: Error getting session:', error);
          finishInitialization();
          return;
        }

        if (session?.user) {
          setUser(session.user);
          currentUserIdRef.current = session.user.id;
          setIsAuthenticated(true);
          finishInitialization();
          loadProfile(session.user.id);
        } else {
          currentUserIdRef.current = null;
          setProfileLoading(false);
          setIsAuthenticated(false);
          finishInitialization();
        }
      } catch (error) {
        console.error('AuthProvider: Error in getInitialSession:', error);
        if (isMounted) {
          if (!currentUserIdRef.current) setIsAuthenticated(false);
          finishInitialization();
        }
      } finally {
        if (isMounted) finishInitialization();
      }
    };

    getInitialSession();

    // Listen for auth changes
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      if (!isMounted) return;

      if (session?.user) {
        const currentUser = session.user;
        setUser(currentUser);
        currentUserIdRef.current = currentUser.id;
        setProfileLoading(true);
        setIsAuthenticated(true);
        finishInitialization();

        setTimeout(() => {
          if (!isMounted || currentUserIdRef.current !== currentUser.id) return;
          loadProfile(currentUser.id);

          if (event === 'SIGNED_IN') {
            const updateLastLogin = async () => {
              const { error } = await supabase
                .from('users')
                .update({ last_login: new Date().toISOString() })
                .eq('id', currentUser.id);
              if (error) console.error('Failed to update last_login:', error);
            };

            void Promise.allSettled([
              updateLastLogin(),
              auditService.logAuditEvent(
                currentUser.id,
                'login',
                'user',
                currentUser.id,
                { event: 'user_signed_in' },
                undefined,
                navigator?.userAgent
              ),
            ]);
          }
        }, 0);
      } else {
        const previousUserId = currentUserIdRef.current;
        currentUserIdRef.current = null;
        setUser(null);
        setProfileLoading(false);
        setIsAuthenticated(false);
        logoutStore();
        finishInitialization();

        if (event === 'SIGNED_OUT' && previousUserId) {
          setTimeout(() => {
            void auditService.logAuditEvent(
              previousUserId,
              'logout',
              'user',
              previousUserId,
              { event: 'user_signed_out' },
              undefined,
              navigator?.userAgent
            ).catch(error => console.error('Exception logging logout:', error));
          }, 0);
        }
      }
    });

    // Cleanup subscription and safety timeout on unmount
    return () => {
      isMounted = false;
      clearTimeout(safetyTimeout);
      subscription.unsubscribe();
    };
  }, [setUserStore, logoutStore, setIsAuthenticated]);

  const value = {
    user,
    loading: !isInitialized ? true : loading,
    profileLoading,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}
